import React, { useState, useEffect } from 'react';
import sanctumApi from '../services/api';
import './Users.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  const fetchUsers = async (page = 1, search = '') => {
    setLoading(true);
    setError('');
    try {
      // Debug: Check if token exists
      const token = localStorage.getItem('sanctum_token');
      console.log('Token exists:', !!token);
      
      const response = await sanctumApi.getUsers(page, 50, search);
      setUsers(response.users);
      setTotalPages(response.totalPages);
      setTotal(response.total);
      setCurrentPage(response.page);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch on page change when not searching
    if (!searchTerm) {
      fetchUsers(currentPage, searchTerm);
    }
  }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Debounced search - always resets to page 1
    const timeoutId = setTimeout(() => {
      fetchUsers(1, searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleViewUser = async (userId) => {
    try {
      const userDetails = await sanctumApi.getUserDetails(userId);
      setSelectedUser(userDetails);
      setShowUserModal(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      setError('Failed to load user details');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleExport = () => {
    const csv = [
      ['Name', 'Username', 'Email', 'Created At', 'Last Active'],
      ...users.map(user => [
        user.name || 'N/A',
        user.nickname || 'N/A',
        user.email,
        formatDate(user.createdAt),
        formatDate(user.lastActive)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `users_${new Date().toISOString().split('T')[0]}.csv`);
    a.click();
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>App Users</h1>
        <div className="header-actions">
          <span className="user-count">{total} users</span>
          <button className="export-btn" onClick={handleExport} disabled={loading}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search by name, nickname, or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="users-table">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Name</th>
                <th>Nickname</th>
                <th>Email</th>
                <th>Posts</th>
                <th>Followers</th>
                <th>Following</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="user-avatar">
                        {user.profilePicture ? (
                          <img src={`https://over-soul-be.onrender.com/${user.profilePicture}`} alt={user.name} />
                        ) : (
                          <div className="avatar-placeholder">
                            {(user.name || user.nickname || 'U')[0].toUpperCase()}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{user.name || 'N/A'}</td>
                    <td>{user.nickname || 'N/A'}</td>
                    <td>{user.email}</td>
                    <td>{user.postsCount || 0}</td>
                    <td>{user.followersCount || 0}</td>
                    <td>{user.followingCount || 0}</td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <button 
                        className="view-btn"
                        onClick={() => handleViewUser(user._id)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="no-data">
                    {searchTerm ? 'No users found matching your search' : 'No users registered yet'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || loading}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages || loading}
          >
            Next
          </button>
        </div>
      )}

      {showUserModal && selectedUser && (
        <div className="user-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>User Details</h2>
              <button className="close-btn" onClick={() => setShowUserModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="user-detail-header">
                <div className="user-avatar-large">
                  {selectedUser.profilePicture ? (
                    <img src={selectedUser.profilePicture} alt={selectedUser.name} />
                  ) : (
                    <div className="avatar-placeholder-large">
                      {(selectedUser.name || selectedUser.nickname || 'U')[0].toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="user-main-info">
                  <h3>{selectedUser.name || 'No Name'}</h3>
                  <p className="username">@{selectedUser.nickname || 'No Nickname'}</p>
                  <p className="email">{selectedUser.email}</p>
                </div>
              </div>
              
              <div className="user-stats">
                <div className="stat-item">
                  <span className="stat-value">{selectedUser.postsCount || 0}</span>
                  <span className="stat-label">Posts</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{selectedUser.followersCount || 0}</span>
                  <span className="stat-label">Followers</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{selectedUser.followingCount || 0}</span>
                  <span className="stat-label">Following</span>
                </div>
              </div>
              
              <div className="user-details-grid">
                <div className="detail-item">
                  <label>Bio:</label>
                  <p>{selectedUser.bio || 'No bio available'}</p>
                </div>
                <div className="detail-item">
                  <label>Account Created:</label>
                  <p>{formatDate(selectedUser.createdAt)}</p>
                </div>
                <div className="detail-item">
                  <label>Last Active:</label>
                  <p>{formatDate(selectedUser.lastActive)}</p>
                </div>
                <div className="detail-item">
                  <label>Email Verified:</label>
                  <p>{selectedUser.isEmailVerified ? 'Yes' : 'No'}</p>
                </div>
                <div className="detail-item">
                  <label>Account Status:</label>
                  <p>{selectedUser.isActive ? 'Active' : 'Inactive'}</p>
                </div>
                <div className="detail-item">
                  <label>User ID:</label>
                  <p className="user-id">{selectedUser._id}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;